import React from "react";
import { observableSignalKeys } from "../engine/registry";
export function selectedSignals(project, result, question = false) {
  const observed = observableSignalKeys(project),
    hidden = new Set(project.timing.hidden || []),
    order = project.timing.order || [];
  return result.signals
    .filter(
      (s) =>
        (question && s.given) ||
        ((s.given ||
          observed.has(s.id) ||
          (s.terminal && project.timing.showTerminals !== false)) &&
          !hidden.has(s.id) &&
          (!s.clock || project.timing.showClocks)),
    )
    .map((s) => ({ ...s, name: project.timing.aliases?.[s.id] || s.name }))
    .sort((a, b) => {
      const ai = order.indexOf(a.id),
        bi = order.indexOf(b.id);
      return (ai < 0 ? 1e6 : ai) - (bi < 0 ? 1e6 : bi);
    });
}
export function displayRows(project, result, question = false) {
  const rows = selectedSignals(project, result, question);
  if (!project.timing.groupBits) return rows;
  const used = new Set(),
    out = [];
  for (const row of rows) {
    if (used.has(row.id)) continue;
    const m = /^(Sum|Y)(\d+)$/.exec(row.pin || "");
    const c = project.components.find((c) => c.id === row.component);
    if (
      m &&
      ["PA", "ENCODER", "PRIORITY", "DEMUX", "DECODER"].includes(c?.type)
    ) {
      const siblings = rows
        .filter(
          (r) =>
            r.component === row.component &&
            new RegExp(`^${m[1]}\\d+$`).test(r.pin),
        )
        .sort((a, b) => +b.pin.slice(m[1].length) - +a.pin.slice(m[1].length));
      if (siblings.length > 1) {
        siblings.forEach((s) => used.add(s.id));
        out.push({
          id: `vector:${c.id}:${m[1]}`,
          name: `${c.name}.${m[1]}[${siblings.length - 1}:0]`,
          keys: siblings.map((s) => s.id),
          given: false,
        });
        continue;
      }
    }
    out.push(row);
    used.add(row.id);
  }
  return out;
}
export function formattedValue(sample, row, format = "binary") {
  if (!row.keys) return sample.values[row.id] ?? "X";
  const binary = row.keys.map((k) => sample.values[k] ?? "X").join("");
  if (binary.includes("X") || format === "binary") return binary;
  const n = parseInt(binary, 2);
  return format === "hex" ? `0x${n.toString(16).toUpperCase()}` : String(n);
}
export const WAVE_LAYOUT = { left: 104, top: 16, cell: 36, levelH: 28, rowH: 56, right: 16, bottom: 12 };
export function clockEdgeTimes(result) {
  const times = new Set();
  for (const row of result.signals.filter(s => s.clock)) {
    result.samples.forEach((sample, i) => {
      if (!i) return;
      const before = result.samples[i - 1].values[row.id], now = sample.values[row.id];
      if ([0, 1].includes(before) && [0, 1].includes(now) && before !== now) times.add(sample.time);
    });
  }
  return [...times].sort((a,b)=>a-b);
}
export function WaveformSVG({ result, rows, question = false, unit = 36, format = "binary", onTime, onCell, selectedTime, edges = true, reveal, ariaLabel, onRename, feedback }) {
  const { left, top, rowH, cell, levelH, right, bottom } = WAVE_LAYOUT;
  const width = left + result.duration * unit + right, height = top + Math.max(1, rows.length) * rowH + bottom;
  const columns = Math.ceil(result.duration * unit / cell), grid = Array.from({length: columns + 1}, (_,i)=>Math.min(i*cell,result.duration*unit));
  const reactId = React.useId().replace(/[^a-z0-9]/gi, "");
  return <svg xmlns="http://www.w3.org/2000/svg" width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={ariaLabel || (question ? "Timing question with blank output waveforms" : "Computed timing diagram")} style={{display:"block"}}>
    <rect width={width} height={height} fill="white" />
    <g fontFamily="Arial, sans-serif" fontSize="12" fill="#3e4e58">
      <g data-grid="uniform">
        {grid.map((x,i)=><line key={i} x1={left+x} x2={left+x} y1={top} y2={height-bottom} stroke="#c4cfca" strokeWidth=".65" strokeDasharray="3 4" />)}
        {Array.from({length: Math.max(1,rows.length)*2+1},(_,i)=><line key={i} x1={left} x2={width-right} y1={top+i*levelH} y2={top+i*levelH} stroke="#c4cfca" strokeWidth=".65" strokeDasharray="3 4" />)}
      </g>
      {edges && <g data-testid="clock-edge-guides" aria-label="Clock edge guides">{clockEdgeTimes(result).map(t=><line key={t} data-clock-edge={t} x1={left+t*unit} x2={left+t*unit} y1={top-5} y2={height-bottom} stroke="#668f7e" strokeWidth="1.25" strokeDasharray="5 4" />)}</g>}
      {rows.map((row,index)=>{
        const high=top+index*rowH, low=high+levelH, color=row.color || ["#2563a5","#9b4d99","#267d59","#bf6928","#7051a4","#b5495a"][index%6];
        const amount=row.given?1:question?0:(reveal?.[row.id]??1), clip=`${reactId}row${index}`;
        let path="";
        if(!row.keys&&amount>0)result.samples.forEach((sample,i)=>{
          const v=sample.values[row.id], x=left+sample.time*unit, end=left+result.times[i+1]*unit;
          if(v===0||v===1){const yy=v?high:low,prev=i?result.samples[i-1].values[row.id]:null;path+=`${i&&(prev===0||prev===1)?` L ${x} ${yy}`:` M ${x} ${yy}`} H ${end}`;}
        });
        return <g key={row.id} data-signal={row.id}>
          <text x={left-27} y={high+levelH/2+4} textAnchor="end" fill={color} fontSize="12" fontWeight="600" style={{cursor:onRename?'text':'default'}} onDoubleClick={onRename?()=>onRename(row):undefined}>{row.name.length>11?row.name.slice(0,10)+"…":row.name}<title>{row.name+(onRename?' · double-click to rename':'')}</title></text>
          {question && [0,1].includes(row.initial) && row.initialUntil>0 && <path data-initial-wave={row.id} d={`M ${left} ${row.initial?high:low} H ${left+Math.min(row.initialUntil,result.duration)*unit}`} fill="none" stroke={color} strokeWidth="1.9"/>}
          <text x={left-17} y={high+4} fontSize="9" fill="#81908a">1</text><text x={left-17} y={low+3} fontSize="9" fill="#81908a">0</text>
          {feedback?.filter(c=>c.signal===row.id).map(c=><rect key={`feedback-${c.index}`} data-feedback={c.correct?'correct':'incorrect'} x={left+c.start*unit} y={high-2} width={(c.end-c.start)*unit} height={levelH+4} fill={c.correct?'#68a686':'#c4746b'} opacity=".14" pointerEvents="none"/>)}
          {amount>0&&<>
            <defs><clipPath id={clip}><rect x={left-2} y={high-2} width={result.duration*unit*amount+4} height={levelH+4}/></clipPath></defs>
            <g clipPath={`url(#${clip})`}>
              {row.keys?result.samples.map((s,i)=>{const x=left+s.time*unit,w=(result.times[i+1]-s.time)*unit,value=formattedValue(s,row,format);return <g key={i}><path d={`M ${x} ${high+levelH/2} l 4 ${-levelH/2} h ${Math.max(0,w-8)} l 4 ${levelH/2} l -4 ${levelH/2} h ${-Math.max(0,w-8)} Z`} fill="white" stroke={color}/>{w>value.length*7+8&&<text x={x+w/2} y={high+levelH/2+4} textAnchor="middle" fill={color}>{value}</text>}</g>;}):<>
                <path data-waveform="true" d={path} fill="none" stroke={color} strokeWidth="1.9" strokeLinejoin="miter"/>
                {result.samples.map((s,i)=>{const v=s.values[row.id];if(v!=="X")return null;const x=left+s.time*unit,w=(result.times[i+1]-s.time)*unit;return <g key={i}><rect x={x} y={high} width={w} height={levelH} fill="#f4efe5"/><path d={`M ${x} ${high} L ${x+w} ${low} M ${x} ${low} L ${x+w} ${high}`} stroke="#b7986e" strokeWidth=".6"/>{w>16&&<text x={x+w/2} y={high+levelH/2+4} textAnchor="middle" fill="#8b713e">X</text>}</g>;})}
              </>}
            </g>
          </>}
        </g>;
      })}
      {selectedTime!==undefined&&<line x1={left+selectedTime*unit} x2={left+selectedTime*unit} y1={top} y2={height-bottom} stroke="#ad8055" strokeWidth="1.1"/>}
      {(onTime||onCell)&&<rect x={left} y={top} width={result.duration*unit} height={rows.length*rowH} fill="transparent" style={{cursor:"crosshair"}} onClick={e=>{
        const rect=e.currentTarget.ownerSVGElement.getBoundingClientRect(),t=Math.max(0,Math.min(result.duration-1e-6,(((e.clientX-rect.left)*width)/rect.width-left)/unit));
        if(onCell){const row=rows[Math.floor((((e.clientY-rect.top)*height)/rect.height-top)/rowH)],i=result.samples.reduce((best,s,n)=>s.time<=t?n:best,0);if(row)onCell(row,i);}else onTime(t);
      }}/>}
    </g>
  </svg>;
}
export function BitEditor({ name, values, count, onChange }) {
  const next = (i) => {
    const v = values[i],
      copy = Array.from({ length: count }, (_, k) => values[k] ?? "X");
    copy[i] = v === 0 ? 1 : v === 1 ? "X" : 0;
    onChange(copy);
  };
  return (
    <div className="bit-editor">
      <strong>{name}</strong>
      <input
        aria-label={`${name} waveform`}
        className="mono"
        spellCheck="false"
        value={values.join("")}
        placeholder="0101XX"
        onChange={(e) => {
          const text = e.target.value
            .toUpperCase()
            .replace(/[^01X]/g, "")
            .slice(0, count);
          onChange([...text].map((v) => (v === "X" ? v : +v)));
        }}
      />
      <div className="bit-cells">
        {Array.from({ length: Math.min(count, 64) }, (_, i) => (
          <button
            key={i}
            className={`bit v${values[i] ?? "X"}`}
            title={`${name}, interval ${i}`}
            onClick={() => next(i)}
          >
            {values[i] ?? "X"}
          </button>
        ))}
        {count > 64 && (
          <small>Edit all {count} intervals in the text field.</small>
        )}
      </div>
    </div>
  );
}
